import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../components/ConfirmDialog";
import { DepartmentRowsSkeleton } from "../components/PageSkeletons";
import PendingSyncBadge from "../components/PendingSyncBadge";

export default function Departments() {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => setDepartments([]));
    api.get("/employees?status=all").then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
  };
  useEffect(() => {
    load();
    // Background retries land on the server every 60s -- poll so a "Pending sync"
    // badge disappears on its own once one succeeds, no manual refresh needed.
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/departments", { name });
      if (data.sync_status === "synced") {
        toast.success(`Department '${name}' created`, { description: "Synced to local server" });
      } else {
        toast.success(`Department '${name}' created`, { description: "Saved — will sync once the local server is back online" });
      }
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (dep) => {
    const ok = await confirm({
      title: `Remove '${dep.name}'?`,
      description: "Employees in this department will need to be reassigned.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/departments/${dep.id}`);
      toast.success(`'${dep.name}' removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const employeesFor = (deptName) => employees.filter((e) => e.department === deptName);

  return (
    <div className="space-y-8 fade-up max-w-2xl">
      <div>
        <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="departments-title">Departments</h1>
        <p className="text-sm text-neutral-500 mt-1">Each department holds one active SOP that its employees are scored against.</p>
      </div>

      <form onSubmit={add} className="flex gap-3">
        <input data-testid="department-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spa & Wellness"
          className="flex-1 bg-white border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" />
        <button data-testid="add-department-button" type="submit" disabled={busy || !name.trim()}
          className="inline-flex items-center gap-2 bg-[#111] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      <div className="bg-white border border-neutral-200 rounded-lg divide-y divide-neutral-100">
        {departments === null && <DepartmentRowsSkeleton />}
        {departments?.map((dep) => {
          const deptEmployees = employeesFor(dep.name);
          return (
            <Link
              key={dep.id}
              to={`/console/departments/${dep.id}`}
              data-testid={`department-row-${dep.id}`}
              className="px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="w-4 h-4 text-neutral-500 shrink-0" />
                <span className="text-sm font-medium truncate">{dep.name}</span>
                <span className="text-xs text-neutral-400 font-mono-data shrink-0">{deptEmployees.length}</span>
                {dep.sync_status === "failed" && <PendingSyncBadge testId={`department-pending-sync-${dep.id}`} />}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`delete-department-${dep.id}`} onClick={(e) => { e.preventDefault(); remove(dep); }} title="Remove"
                  className="p-2 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </div>
            </Link>
          );
        })}
        {departments?.length === 0 && <p className="px-6 py-8 text-center text-sm text-neutral-400">No departments configured.</p>}
      </div>
    </div>
  );
}
